import { useState, useEffect } from 'react'
import { useNavigate, useParams, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import Layout from '@/components/Layout'
import { CurrentUserProvider } from '@/hooks/useCurrentUser'
import NAVCredentialsForm from '@/components/NAVCredentialsForm'
import { apiFetch } from '@/utils/api'
import { useI18n } from '@/hooks/useI18n'

interface Company {
  id: string
  name: string
  tax_id?: string
  registration_no?: string
  address?: string
  vat_reporting_period: 'monthly' | 'quarterly'
  is_active: boolean
  created_at: string
}

interface FormData {
  name: string
  tax_id: string
  registration_no: string
  address: string
  vat_reporting_period: 'monthly' | 'quarterly'
  is_active: boolean
}

function CompanyDetailPage() {
  const navigate = useNavigate()
  const params = useParams({ from: '/admin/finance/companies/$id' })
  const { id } = params
  const { t } = useI18n()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [company, setCompany] = useState<Company | null>(null)
  const [formData, setFormData] = useState<FormData>({
    name: '',
    tax_id: '',
    registration_no: '',
    address: '',
    vat_reporting_period: 'quarterly',
    is_active: true,
  })
  const [activeTab, setActiveTab] = useState('settings')

  useEffect(() => {
    loadCompany()
  }, [id])

  const loadCompany = async () => {
    try {
      setLoading(true)
      const resp = await apiFetch(`/api/companies/?include_inactive=true`)
      if (resp.ok) {
        const companies = await resp.json()
        const found = companies.find((c: Company) => c.id === id)
        if (found) {
          setCompany(found)

          setFormData({
            name: found.name || '',
            tax_id: found.tax_id || '',
            registration_no: found.registration_no || '',
            address: found.address || '',
            vat_reporting_period: found.vat_reporting_period || 'quarterly',
            is_active: found.is_active,
          })
        } else {
          setError(t('finance.companies.notFound', 'admin'))
        }
      }
    } catch (err) {
      console.error('Error loading company', err)
      setError(t('finance.companies.loadFailed', 'admin'))
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setSaving(true)

      // Update company
      const companyPayload = {
        name: formData.name.trim(),
        tax_id: formData.tax_id.trim() || undefined,
        registration_no: formData.registration_no.trim() || undefined,
        address: formData.address.trim() || undefined,
        vat_reporting_period: formData.vat_reporting_period,
        is_active: formData.is_active,
      }
      const companyResp = await apiFetch(`/api/companies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(companyPayload),
      })

      if (!companyResp.ok) {
        const data = await companyResp.json().catch(() => ({}))
        alert(data.detail || t('finance.companies.updateFailed', 'admin'))
        return
      }

      await loadCompany()
      alert(t('finance.companies.updateSuccess', 'admin'))
    } catch (err) {
      console.error('Error updating company', err)
      alert(t('finance.companies.updateFailed', 'admin'))
    } finally {
      setSaving(false)
    }
  }

  const deleteCompany = async () => {
    if (!confirm(t('finance.companies.deleteConfirm', 'admin'))) return
    try {
      const resp = await apiFetch(`/api/companies/${id}`, { method: 'DELETE' })
      if (resp.ok) {
        navigate({ to: '/admin/finance' })
      } else {
        alert(t('finance.companies.deleteFailed', 'admin'))
      }
    } catch (err) {
      console.error('Error deleting company', err)
      alert(t('finance.companies.deleteFailed', 'admin'))
    }
  }

  const handleBack = () => {
    navigate({ to: '/admin/finance' })
  }

  if (loading) {
    return (
      <CurrentUserProvider>
        <Layout>
          <div className="p-8 text-center">{t('actions.loading', 'common')}</div>
        </Layout>
      </CurrentUserProvider>
    )
  }

  if (error || !company) {
    return (
      <CurrentUserProvider>
        <Layout>
          <div className="p-8 text-center text-red-600">
            {error || t('finance.companies.notFound', 'admin')}
          </div>
        </Layout>
      </CurrentUserProvider>
    )
  }

  return (
    <CurrentUserProvider>
      <Layout>
        <div className="space-y-6">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={handleBack} className="h-8 w-8 p-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {t('finance.companies.editTitle', 'admin', { name: company.name })}
              </h1>
              <p className="text-muted-foreground">{t('finance.companies.editSubtitle', 'admin')}</p>
            </div>
            <div className="ml-auto">
              <Button variant="outline" className="text-destructive hover:text-destructive/90" onClick={deleteCompany}>
                <Trash2 className="h-4 w-4 mr-1" /> {t('actions.delete', 'common')}
              </Button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="settings">{t('finance.companies.tabs.settings', 'admin')}</TabsTrigger>
              <TabsTrigger value="nav">{t('finance.companies.tabs.nav', 'admin')}</TabsTrigger>
            </TabsList>

            <TabsContent value="settings">
              <Card>
                <CardHeader>
                  <CardTitle>{t('finance.companies.settingsTitle', 'admin')}</CardTitle>
                  <CardDescription>{t('finance.companies.settingsDescription', 'admin')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSaveSettings} className="space-y-6">
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="name">{t('finance.companies.fields.name', 'admin')}</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="tax_id">{t('finance.companies.fields.taxId', 'admin')}</Label>
                        <Input
                          id="tax_id"
                          value={formData.tax_id}
                          onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                          placeholder={t('finance.companies.placeholders.taxId', 'admin')}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="registration_no">{t('finance.companies.fields.registrationNo', 'admin')}</Label>
                        <Input
                          id="registration_no"
                          value={formData.registration_no}
                          onChange={(e) => setFormData({ ...formData, registration_no: e.target.value })}
                          placeholder={t('finance.companies.placeholders.registrationNo', 'admin')}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="address">{t('finance.companies.fields.address', 'admin')}</Label>
                        <Input
                          id="address"
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          placeholder={t('finance.companies.placeholders.address', 'admin')}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="vat_reporting_period">{t('finance.companies.vatReportingPeriod', 'admin')}</Label>
                        <Select
                          value={formData.vat_reporting_period}
                          onValueChange={(value: 'monthly' | 'quarterly') => setFormData({ ...formData, vat_reporting_period: value })}
                        >
                          <SelectTrigger id="vat_reporting_period" className="w-[200px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">{t('finance.companies.vatPeriodMonthly', 'admin')}</SelectItem>
                            <SelectItem value="quarterly">{t('finance.companies.vatPeriodQuarterly', 'admin')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="is_active"
                          checked={formData.is_active}
                          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked as boolean })}
                        />
                        <Label htmlFor="is_active">{t('status.active', 'common')}</Label>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-4">
                      <Button type="button" variant="outline" onClick={handleBack}>
                        {t('actions.cancel', 'common')}
                      </Button>
                      <Button type="submit" disabled={saving}>
                        {saving ? t('actions.saving', 'common') : t('actions.saveChanges', 'common')}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="nav">
              <Card>
                <CardHeader>
                  <CardTitle>{t('finance.companies.navTitle', 'admin')}</CardTitle>
                  <CardDescription>{t('finance.companies.navDescription', 'admin')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <NAVCredentialsForm companyId={company.id} onCredentialsChange={() => {}} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </Layout>
    </CurrentUserProvider>
  )
}

export const Route = createFileRoute('/admin/finance/companies/$id')({
  component: CompanyDetailPage,
})
